/**
 * The card a report chart sits in: a title, a sentence, the chart, and its
 * own PNG button. Marked with `data-report-chart` so the frame's "every chart
 * as PNG" finds it, and so the print view can lay it out.
 */

import { useCallback, useRef, type ReactNode } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { btnSecondary } from '../../../mono/primitives';
import { downloadChartPng, slugifyFilename } from './exportChartPng';

export interface ReportChartCardProps {
  title: string;
  description?: string;
  /** File name for the PNG; defaults to the title. */
  pngName?: string;
  /** Hide the PNG button — print views pass this. */
  print?: boolean;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function ReportChartCard({ title, description, pngName, print = false, actions, children, className = '' }: ReportChartCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const handlePng = useCallback(() => {
    if (ref.current) void downloadChartPng(ref.current, slugifyFilename(pngName ?? title));
  }, [pngName, title]);

  return (
    <section
      className={`border border-[var(--rule)] bg-[var(--bg-raised)] p-6 ${print ? 'break-inside-avoid' : ''} ${className}`}
      data-report-chart=""
      data-chart-title={pngName ?? title}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)]">{title}</h2>
          {description && <p className="mt-0.5 text-sm text-[var(--text-ter)]">{description}</p>}
        </div>
        {!print && (
          <div className="flex items-center gap-2">
            {actions}
            <button type="button" {...btnSecondary('h-7 px-2 text-[0.65rem]')} onClick={handlePng} title="Download this chart as a PNG">
              <ImageIcon className="h-3.5 w-3.5" /> PNG
            </button>
          </div>
        )}
      </div>
      <div ref={ref} data-chart-surface="">{children}</div>
    </section>
  );
}
